import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import PrimaryButton from '@components/buttons/PrimaryButton';
import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import FormButtons from '@components/fields/FormButtons';
import IconRadioButtonGroup from '@components/fields/IconRadioButtonGroup';
import Input from '@components/fields/Input';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import Form from '@components/forms/Form';
import styles from '@components/forms/PersonalizeForm.module.css';
import {
  CareProfileActionableFieldEnum,
  CareProfileCurrentCareTransitionEnum,
  CareProfileFragment,
  CareProfileHelpLevelRequiredEnum,
  CareProfileMedicalConditionEnum,
  CareProfileProviderFinancialConcernEnum,
  CareProfileResidenceTypeEnum,
  CareProfileSenseOfFulfillmentEnum,
  CareProfileSocialSatisfactionEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  ClientRoleProviderEmotionalStateEnum,
  ClientRoleProviderFeelsSupportedEnum,
  ClientRoleUpdateDocument,
  ClientRoleUpdateMutation,
  ClientRoleUpdateMutationVariables
} from '@typing/Generated';
import { PersonalizeExperienceFields } from '@typing/Models';
import { handleMutationResponse } from '@utils/formUtils';
import { IconName } from '@utils/iconUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';
import { providerEmotionalStateIcon, providerFeelsSupportedIcon } from '@utils/modelUtils';
import {
  optionsForBoolean,
  optionsForFinancialConcern,
  optionsForResidenceType,
  optionsForSocialSatisfaction
} from '@utils/optionUtils';

type FormData = {
  currentCareTransitions: CareProfileCurrentCareTransitionEnum[];
  currentCareTransitionsOther: string | null;
  helpLevelRequired: CareProfileHelpLevelRequiredEnum | null;
  legalHelpNeeded: boolean | null;
  medicalConditions: CareProfileMedicalConditionEnum[] | [];
  medicalConditionsOther: string | null;
  providerEmotionalState: ClientRoleProviderEmotionalStateEnum | null;
  providerFeelsSupported: ClientRoleProviderFeelsSupportedEnum | null;
  providerFinancesImpacted: boolean | null;
  providerFinancialConcerns: CareProfileProviderFinancialConcernEnum[];
  providerFinancialConcernsOther: string | null;
  residenceType: CareProfileResidenceTypeEnum | null;
  residenceTypeOther: string | null;
  senseOfFulfillment: CareProfileSenseOfFulfillmentEnum | null;
  socialSatisfaction: CareProfileSocialSatisfactionEnum | null;
};

type ClientRole = {
  id: string;
  providerEmotionalState: ClientRoleProviderEmotionalStateEnum | null;
  providerFeelsSupported: ClientRoleProviderFeelsSupportedEnum | null;
};

type ProviderFeelsSupportedOption = {
  iconName: IconName;
  label: string;
  value: ClientRoleProviderFeelsSupportedEnum;
};

type ProviderEmotionalStateOption = {
  iconName: IconName;
  label: string;
  value: ClientRoleProviderEmotionalStateEnum;
};

type Props = {
  careProfile: CareProfileFragment;
  clientRole: ClientRole | null;
  inTracker?: boolean;
  onCancel?: () => void;
  onSuccess: (careProfile: CareProfileFragment | null, clientRole: ClientRole | null) => void;
  questions: (PersonalizeExperienceFields | CareProfileActionableFieldEnum)[];
};

const PersonalizeForm = ({ careProfile, clientRole, inTracker = false, onCancel, onSuccess, questions }: Props) => {
  const initialValues = {
    currentCareTransitions: careProfile.currentCareTransitions?.currentCareTransitionsOptions ?? [],
    currentCareTransitionsOther: careProfile.currentCareTransitions?.other ?? null,
    helpLevelRequired: careProfile?.helpLevelRequired ?? null,
    legalHelpNeeded: careProfile?.legalHelpNeeded !== null ? careProfile.legalHelpNeeded : null,
    medicalConditions: careProfile?.medicalConditions?.medicalConditionsOptions ?? [],
    medicalConditionsOther: careProfile?.medicalConditions?.other ?? null,
    providerEmotionalState: clientRole?.providerEmotionalState ?? null,
    providerFeelsSupported: clientRole?.providerFeelsSupported ?? null,
    providerFinancesImpacted:
      careProfile.providerFinancesImpacted !== null ? careProfile.providerFinancesImpacted : null,
    providerFinancialConcerns: careProfile?.providerFinancialConcerns?.providerFinancialConcernsOptions ?? [],
    providerFinancialConcernsOther: careProfile?.providerFinancialConcerns?.other ?? null,
    residenceType: careProfile?.residenceType?.residenceTypeOption ?? null,
    residenceTypeOther: careProfile.residenceType?.other ?? null,
    senseOfFulfillment: careProfile?.senseOfFulfillment ?? null,
    socialSatisfaction: careProfile?.socialSatisfaction ?? null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateClientRole] = useMutation<ClientRoleUpdateMutation, ClientRoleUpdateMutationVariables>(
    ClientRoleUpdateDocument
  );

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      currentCareTransitions: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileCurrentCareTransitionEnum)).defined()),
      currentCareTransitionsOther: yup.string().nullable().defined(),
      helpLevelRequired: yup.string().nullable().defined().oneOf(Object.values(CareProfileHelpLevelRequiredEnum)),
      legalHelpNeeded: yup.boolean().nullable().defined(),
      medicalConditions: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileMedicalConditionEnum)).defined()),
      medicalConditionsOther: yup.string().nullable().defined(),
      providerEmotionalState: yup
        .string()
        .nullable()
        .defined()
        .oneOf(Object.values(ClientRoleProviderEmotionalStateEnum)),
      providerFeelsSupported: yup
        .string()
        .nullable()
        .defined()
        .oneOf(Object.values(ClientRoleProviderFeelsSupportedEnum)),
      providerFinancesImpacted: yup.boolean().nullable().defined(),
      providerFinancialConcerns: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileProviderFinancialConcernEnum)).defined()),
      providerFinancialConcernsOther: yup.string().nullable().defined(),
      residenceType: yup.string().nullable().defined().oneOf(Object.values(CareProfileResidenceTypeEnum)),
      residenceTypeOther: yup.string().nullable().defined(),
      senseOfFulfillment: yup.string().nullable().defined().oneOf(Object.values(CareProfileSenseOfFulfillmentEnum)),
      socialSatisfaction: yup.string().nullable().defined().oneOf(Object.values(CareProfileSocialSatisfactionEnum))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const residenceType = form.watch('residenceType');
  const residenceTypeOther = form.watch('residenceTypeOther');

  useEffect(() => {
    if (residenceTypeOther && residenceTypeOther !== '') {
      form.setValue('residenceType', CareProfileResidenceTypeEnum.other);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residenceTypeOther]);

  useEffect(() => {
    if (residenceType !== CareProfileResidenceTypeEnum.other) {
      form.setValue('residenceTypeOther', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residenceType]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    if (
      clientRole &&
      (questions.includes(PersonalizeExperienceFields.PROVIDER_EMOTIONAL_STATE) ||
        questions.includes(PersonalizeExperienceFields.PROVIDER_FEELS_SUPPORTED))
    ) {
      return updateClientRole({
        variables: {
          id: clientRole.id,
          providerEmotionalState: form.getValues().providerEmotionalState,
          providerFeelsSupported: form.getValues().providerFeelsSupported
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.clientRoleUpdate,
          intl,
          onSuccess: () => onSuccess(null, response.data?.clientRoleUpdate?.clientRole ?? null),
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.careProfile'
        });
      });
    }
    return updateCareProfile({
      variables: {
        ...form.getValues(),
        currentCareTransitions: {
          currentCareTransitionOptions: form.getValues().currentCareTransitions,
          other: form.getValues().currentCareTransitionsOther
        },
        id: careProfile.id,
        medicalConditions: {
          medicalConditionOptions: form.getValues().medicalConditions,
          other: form.getValues().medicalConditionsOther
        },
        providerFinancialConcerns: {
          other: form.getValues().providerFinancialConcernsOther,
          providerFinancialConcernsOptions: form.getValues().providerFinancialConcerns
        },
        residenceType: {
          other: form.getValues().residenceTypeOther,
          residenceTypeOption:
            form.getValues().residenceTypeOther && form.getValues().residenceTypeOther !== ''
              ? CareProfileResidenceTypeEnum.other
              : form.getValues().residenceType
        }
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onSuccess: () => onSuccess(response.data?.careProfileUpdate?.careProfile ?? null, null),
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [careProfile.id, clientRole, form, intl, onSuccess, questions, updateCareProfile, updateClientRole]);

  const providerEmotionalStateOptions: ProviderEmotionalStateOption[] = useMemo(() => {
    const options = [
      { value: ClientRoleProviderEmotionalStateEnum.very_negative },
      { value: ClientRoleProviderEmotionalStateEnum.negative },
      { value: ClientRoleProviderEmotionalStateEnum.neutral },
      { value: ClientRoleProviderEmotionalStateEnum.positive },
      { value: ClientRoleProviderEmotionalStateEnum.very_positive }
    ];
    return options.map(option => ({
      iconName: providerEmotionalStateIcon(option.value),
      label: intl.formatMessage({
        id: `models.pme.questions.provider_emotional_state.options.${option.value}.content`
      }),
      value: option.value
    }));
  }, [intl]);

  const senseOfFulfillmentOptions = useMemo(() => {
    const options = [
      { icon: 1, option: CareProfileSenseOfFulfillmentEnum.not_at_all },
      { icon: 2, option: CareProfileSenseOfFulfillmentEnum.level_2 },
      { icon: 3, option: CareProfileSenseOfFulfillmentEnum.neutral },
      { icon: 4, option: CareProfileSenseOfFulfillmentEnum.level_4 },
      { icon: 5, option: CareProfileSenseOfFulfillmentEnum.extremely }
    ];
    return options.map(option => ({
      iconName: option.icon,
      label: intl.formatMessage({
        id: `models.pme.questions.sense_of_fulfillment.options.${option.option}.content`
      }),
      value: option.option
    }));
  }, [intl]);

  const helpLevelRequiredOptions = useMemo(() => {
    const options = [
      { option: CareProfileHelpLevelRequiredEnum.independent },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.help_level_required.options.basic_support.help' }),
        option: CareProfileHelpLevelRequiredEnum.basic_support
      },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.help_level_required.options.constant_care.help' }),
        option: CareProfileHelpLevelRequiredEnum.constant_care
      },
      { option: CareProfileHelpLevelRequiredEnum.crisis },
      { option: CareProfileHelpLevelRequiredEnum.end_of_life },
      { option: CareProfileHelpLevelRequiredEnum.deceased }
    ];
    return options.map(option => ({
      help: option.help,
      label: intl.formatMessage({
        id: `models.pme.questions.help_level_required.options.${option.option}.content`
      }),
      value: option.option
    }));
  }, [intl]);

  const residenceTypeOptions = useMemo(() => optionsForResidenceType(intl), [intl]);

  const currentCareTransitionOptions = useMemo(() => {
    const options = [
      { option: CareProfileCurrentCareTransitionEnum.none },
      { option: CareProfileCurrentCareTransitionEnum.adding_part_time_care },
      { option: CareProfileCurrentCareTransitionEnum.adding_full_time_care },
      { option: CareProfileCurrentCareTransitionEnum.reducing_care },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.current_care_transitions.options.moving.help' }),
        option: CareProfileCurrentCareTransitionEnum.moving
      },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.current_care_transitions.options.transferring.help' }),
        option: CareProfileCurrentCareTransitionEnum.transferring
      },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.current_care_transitions.options.going_home.help' }),

        option: CareProfileCurrentCareTransitionEnum.going_home
      }
    ];
    return options.map(option => ({
      help: option.help,
      label: intl.formatMessage({
        id: `models.pme.questions.current_care_transitions.options.${option.option}.content`
      }),
      value: option.option
    }));
  }, [intl]);

  const socialSatisfactionOptions = useMemo(() => optionsForSocialSatisfaction(intl), [intl]);

  const providerFeelsSupportedOptions: ProviderFeelsSupportedOption[] = useMemo(() => {
    const options = [
      { value: ClientRoleProviderFeelsSupportedEnum.yes },
      { value: ClientRoleProviderFeelsSupportedEnum.no },
      { value: ClientRoleProviderFeelsSupportedEnum.its_complicated }
    ];
    return options.map(option => ({
      iconName: providerFeelsSupportedIcon(option.value),
      label: intl.formatMessage({
        id: `models.pme.questions.provider_feels_supported.options.${option.value}.content`
      }),
      value: option.value
    }));
  }, [intl]);

  const medicalConditionOptions = useMemo(() => {
    const options = [
      { option: CareProfileMedicalConditionEnum.heart_condition },
      { option: CareProfileMedicalConditionEnum.cancer },
      { option: CareProfileMedicalConditionEnum.lung_condition },
      { option: CareProfileMedicalConditionEnum.stroke },
      { option: CareProfileMedicalConditionEnum.diabetes },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.medical_conditions.options.dementia.help' }),
        option: CareProfileMedicalConditionEnum.dementia
      },
      { option: CareProfileMedicalConditionEnum.kidney_condition },
      { option: CareProfileMedicalConditionEnum.hearing_or_vision_loss },
      {
        help: intl.formatMessage({ id: 'models.pme.questions.medical_conditions.options.mental_health.help' }),
        option: CareProfileMedicalConditionEnum.mental_health
      },
      { option: CareProfileMedicalConditionEnum.im_not_sure },
      { option: CareProfileMedicalConditionEnum.none }
    ];
    return options.map(option => ({
      help: option.help,
      label: intl.formatMessage({
        id: `models.pme.questions.medical_conditions.options.${option.option}.content`
      }),
      value: option.option
    }));
  }, [intl]);

  const financialConcernOptions = useMemo(() => optionsForFinancialConcern(intl), [intl]);

  const booleanOptions = useMemo(() => optionsForBoolean(intl), [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div
        className={questions.includes(PersonalizeExperienceFields.PROVIDER_EMOTIONAL_STATE) ? undefined : 'ion-hide'}
      >
        <IconRadioButtonGroup<FormData>
          control={form.control}
          error={form.formState.errors.providerEmotionalState}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.provider_emotional_state.content' })}
          name="providerEmotionalState"
          options={providerEmotionalStateOptions}
        />
      </div>
      <div
        className={questions.includes(PersonalizeExperienceFields.PROVIDER_FEELS_SUPPORTED) ? undefined : 'ion-hide'}
      >
        <IconRadioButtonGroup<FormData>
          control={form.control}
          error={form.formState.errors.providerFeelsSupported}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.provider_feels_supported.content' })}
          name="providerFeelsSupported"
          options={providerFeelsSupportedOptions}
        />
      </div>
      <div className={questions.includes(CareProfileActionableFieldEnum.sense_of_fulfillment) ? undefined : 'ion-hide'}>
        <IconRadioButtonGroup<FormData>
          control={form.control}
          error={form.formState.errors.senseOfFulfillment}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.sense_of_fulfillment.content' })}
          name="senseOfFulfillment"
          options={senseOfFulfillmentOptions}
        />
      </div>
      <div
        className={questions.includes(CareProfileActionableFieldEnum.social_satisfaction) ? undefined : 'ion-hide'}
        data-test="socialSatisfaction"
      >
        <IconRadioButtonGroup<FormData>
          control={form.control}
          error={form.formState.errors.socialSatisfaction}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.social_satisfaction.content' })}
          name="socialSatisfaction"
          options={socialSatisfactionOptions}
        />
      </div>
      <div
        className={
          questions.includes(CareProfileActionableFieldEnum.provider_financial_concerns) ? undefined : 'ion-hide'
        }
      >
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.providerFinancialConcerns}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.provider_financial_concerns.content' })}
          name="providerFinancialConcerns"
          options={financialConcernOptions}
        />
        <div className={styles.otherInput}>
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.providerFinancialConcernsOther}
            isOption
            name="providerFinancialConcernsOther"
            placeholder={intl.formatMessage({
              id: 'models.pme.questions.provider_financial_concerns.options.other.content'
            })}
          />
        </div>
      </div>
      <div
        className={
          questions.includes(CareProfileActionableFieldEnum.provider_finances_impacted) ? undefined : 'ion-hide'
        }
        data-test="providerFinancesImpacted"
      >
        <BooleanRadioButtonGroup<FormData>
          control={form.control}
          error={form.formState.errors.providerFinancesImpacted}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.provider_finances_impacted.content' })}
          name="providerFinancesImpacted"
          options={booleanOptions}
        />
      </div>
      <div className={questions.includes(CareProfileActionableFieldEnum.help_level_required) ? undefined : 'ion-hide'}>
        <RadioButtonGroup<FormData>
          control={form.control}
          error={form.formState.errors.helpLevelRequired}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.help_level_required.content' })}
          name="helpLevelRequired"
          options={helpLevelRequiredOptions}
        />
      </div>
      <div className={questions.includes(CareProfileActionableFieldEnum.residence_type) ? undefined : 'ion-hide'}>
        <RadioButtonGroup<FormData>
          control={form.control}
          data-test="residenceType"
          error={form.formState.errors.residenceType}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.residence_type.content' })}
          name="residenceType"
          options={residenceTypeOptions}
        />
        <div className={styles.otherInput}>
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.residenceTypeOther}
            isOption
            name="residenceTypeOther"
            placeholder={intl.formatMessage({
              id: 'models.pme.questions.residence_type.options.other.content'
            })}
          />
        </div>
      </div>
      <div
        className={questions.includes(CareProfileActionableFieldEnum.current_care_transitions) ? undefined : 'ion-hide'}
      >
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.currentCareTransitions}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.current_care_transitions.content' })}
          name="currentCareTransitions"
          options={currentCareTransitionOptions}
          smallerLabel
        />
        <div className={styles.otherInput}>
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.currentCareTransitionsOther}
            isOption
            name="currentCareTransitionsOther"
            placeholder={intl.formatMessage({
              id: 'models.pme.questions.current_care_transitions.options.other.content'
            })}
          />
        </div>
      </div>
      <div className={questions.includes(CareProfileActionableFieldEnum.legal_help_needed) ? undefined : 'ion-hide'}>
        <BooleanRadioButtonGroup<FormData>
          control={form.control}
          error={form.formState.errors.legalHelpNeeded}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.legal_help_needed.content' })}
          name="legalHelpNeeded"
          options={booleanOptions}
        />
      </div>
      <div className={questions.includes(CareProfileActionableFieldEnum.medical_conditions) ? undefined : 'ion-hide'}>
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.medicalConditions}
          inTracker
          label={intl.formatMessage({ id: 'models.pme.questions.medical_conditions.content' })}
          name="medicalConditions"
          options={medicalConditionOptions}
        />
        <div className={styles.otherInput}>
          <Input<FormData>
            control={form.control}
            data-test="medicalConditionsOther"
            error={form.formState.errors.medicalConditionsOther}
            isOption
            name="medicalConditionsOther"
            placeholder={intl.formatMessage({
              id: 'models.pme.questions.medical_conditions.options.other.content'
            })}
          />
        </div>
      </div>

      {inTracker ? (
        <div className={styles.personalizeButton}>
          <PrimaryButton
            data-test="submit-button"
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
            textKey="pages.carePlan.learn.personalize"
            type="submit"
          />
        </div>
      ) : (
        <FormButtons hookForm={form} onCancel={onCancel} />
      )}
    </Form>
  );
};

export default PersonalizeForm;
